import { type InputHTMLAttributes, type ReactNode, forwardRef, useId } from 'react';

import { cn } from '@/lib/cn';

export interface FieldProps {
  label?: ReactNode;
  hint?: ReactNode;
  error?: string | null;
  required?: boolean;
  className?: string;
  children: ReactNode;
  htmlFor?: string;
}

/** Общая обёртка поля: подпись сверху, подсказка/ошибка снизу. */
export function Field({ label, hint, error, required, className, children, htmlFor }: FieldProps) {
  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      {label && (
        <label htmlFor={htmlFor} className="text-[13px] font-medium text-muted">
          {label}
          {required && <span className="ml-0.5 text-danger">*</span>}
        </label>
      )}
      {children}
      {error ? (
        <p className="text-[12px] text-danger">{error}</p>
      ) : hint ? (
        <p className="text-[12px] text-subtle">{hint}</p>
      ) : null}
    </div>
  );
}

export const inputClasses = (hasError?: boolean, extra?: string) =>
  cn(
    'w-full rounded-[10px] border bg-surface px-3 py-2 text-sm text-text',
    'placeholder:text-subtle transition-colors duration-150',
    'focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent',
    'disabled:opacity-60 disabled:cursor-not-allowed',
    hasError ? 'border-danger focus:ring-danger/30 focus:border-danger' : 'border-border',
    extra,
  );

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: ReactNode;
  hint?: ReactNode;
  error?: string | null;
  iconLeft?: ReactNode;
  iconRight?: ReactNode;
  wrapperClassName?: string;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { label, hint, error, iconLeft, iconRight, className, wrapperClassName, required, id, ...props },
  ref,
) {
  const generatedId = useId();
  const inputId = id ?? generatedId;

  const field = (
    <div className="relative">
      {iconLeft && (
        <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-subtle [&>svg]:h-4 [&>svg]:w-4">
          {iconLeft}
        </span>
      )}
      <input
        ref={ref}
        id={inputId}
        aria-invalid={error ? true : undefined}
        className={inputClasses(Boolean(error), cn(iconLeft && 'pl-9', iconRight && 'pr-9', className))}
        {...props}
      />
      {iconRight && (
        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-subtle [&>svg]:h-4 [&>svg]:w-4">
          {iconRight}
        </span>
      )}
    </div>
  );

  if (!label && !hint && !error) return field;

  return (
    <Field label={label} hint={hint} error={error} required={required} htmlFor={inputId} className={wrapperClassName}>
      {field}
    </Field>
  );
});

export interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: ReactNode;
  hint?: ReactNode;
  error?: string | null;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { label, hint, error, className, required, id, rows = 3, ...props },
  ref,
) {
  const generatedId = useId();
  const textareaId = id ?? generatedId;

  const field = (
    <textarea
      ref={ref}
      id={textareaId}
      rows={rows}
      aria-invalid={error ? true : undefined}
      className={inputClasses(Boolean(error), cn('resize-y min-h-[72px]', className))}
      {...props}
    />
  );

  if (!label && !hint && !error) return field;

  return (
    <Field label={label} hint={hint} error={error} required={required} htmlFor={textareaId}>
      {field}
    </Field>
  );
});
